/**
 * Порядок дочерних элементов по схеме WordprocessingML.
 *
 * Word строго проверяет последовательность элементов в w:pPr, w:rPr и w:sectPr:
 * элемент не на своём месте приводит к ошибке «обнаружено нечитаемое содержимое».
 * Поэтому все вставки при исправлениях идут через setOrdered().
 */

import { cloneNode, el, firstChild, insertAt, type XElement } from './xml';

export const PPR_ORDER = [
  'w:pStyle', 'w:keepNext', 'w:keepLines', 'w:pageBreakBefore', 'w:framePr', 'w:widowControl',
  'w:numPr', 'w:suppressLineNumbers', 'w:pBdr', 'w:shd', 'w:tabs', 'w:suppressAutoHyphens',
  'w:kinsoku', 'w:wordWrap', 'w:overflowPunct', 'w:topLinePunct', 'w:autoSpaceDE', 'w:autoSpaceDN',
  'w:bidi', 'w:adjustRightInd', 'w:snapToGrid', 'w:spacing', 'w:ind', 'w:contextualSpacing',
  'w:mirrorIndents', 'w:suppressOverlap', 'w:jc', 'w:textDirection', 'w:textAlignment',
  'w:textboxTightWrap', 'w:outlineLvl', 'w:divId', 'w:cnfStyle', 'w:rPr', 'w:sectPr', 'w:pPrChange',
];

const RPR_BASE = [
  'w:rStyle', 'w:rFonts', 'w:b', 'w:bCs', 'w:i', 'w:iCs', 'w:caps', 'w:smallCaps', 'w:strike',
  'w:dstrike', 'w:outline', 'w:shadow', 'w:emboss', 'w:imprint', 'w:noProof', 'w:snapToGrid',
  'w:vanish', 'w:webHidden', 'w:color', 'w:spacing', 'w:w', 'w:kern', 'w:position', 'w:sz',
  'w:szCs', 'w:highlight', 'w:u', 'w:effect', 'w:bdr', 'w:shd', 'w:fitText', 'w:vertAlign',
  'w:rtl', 'w:cs', 'w:em', 'w:lang', 'w:eastAsianLayout', 'w:specVanish', 'w:oMath',
];

export const RPR_ORDER = [...RPR_BASE, 'w:rPrChange'];

/** Свойства знака абзаца: сначала пометки рецензирования, затем обычные свойства. */
export const PARA_RPR_ORDER = ['w:ins', 'w:del', 'w:moveFrom', 'w:moveTo', ...RPR_BASE, 'w:rPrChange'];

export const SECTPR_ORDER = [
  'w:headerReference', 'w:footerReference', 'w:footnotePr', 'w:endnotePr', 'w:type', 'w:pgSz',
  'w:pgMar', 'w:paperSrc', 'w:pgBorders', 'w:lnNumType', 'w:pgNumType', 'w:cols', 'w:formProt',
  'w:vAlign', 'w:noEndnote', 'w:titlePg', 'w:textDirection', 'w:bidi', 'w:rtlGutter', 'w:docGrid',
  'w:printerSettings', 'w:sectPrChange',
];

/** Определение стиля w:style в styles.xml. */
export const STYLE_ORDER = [
  'w:name', 'w:aliases', 'w:basedOn', 'w:next', 'w:link', 'w:autoRedefine', 'w:hidden', 'w:uiPriority',
  'w:semiHidden', 'w:unhideWhenUsed', 'w:qFormat', 'w:locked', 'w:personal', 'w:personalCompose',
  'w:personalReply', 'w:rsid', 'w:pPr', 'w:rPr', 'w:tblPr', 'w:trPr', 'w:tcPr', 'w:tblStylePr',
];

/** Уровень списка w:lvl в numbering.xml. */
export const LVL_ORDER = [
  'w:start', 'w:numFmt', 'w:lvlRestart', 'w:pStyle', 'w:isLgl', 'w:suff', 'w:lvlText', 'w:lvlPicBulletId',
  'w:legacy', 'w:lvlJc', 'w:pPr', 'w:rPr',
];

/**
 * Вставляет или заменяет дочерний элемент, соблюдая порядок схемы.
 * Неизвестные элементы (расширения w14 и т. п.) не сдвигаются.
 */
export function setOrdered(parent: XElement, child: XElement, order: readonly string[]): XElement {
  const existing = firstChild(parent, child.name);
  if (existing) {
    const idx = parent.children.indexOf(existing);
    parent.children[idx] = child;
    child.parent = parent;
    existing.parent = null;
    return child;
  }
  const rank = order.indexOf(child.name);
  let insertIdx = parent.children.length;
  if (rank >= 0) {
    for (let i = 0; i < parent.children.length; i++) {
      const c = parent.children[i]!;
      if (c.type !== 'element') continue;
      const r = order.indexOf(c.name);
      if (r > rank) {
        insertIdx = i;
        break;
      }
    }
  }
  return insertAt(parent, child, insertIdx);
}

/** Возвращает существующий дочерний элемент или создаёт его на правильном месте. */
export function getOrCreate(parent: XElement, name: string, order: readonly string[]): XElement {
  return firstChild(parent, name) ?? setOrdered(parent, el(name), order);
}

export function removeChild(parent: XElement, name: string): void {
  const c = firstChild(parent, name);
  if (c) {
    parent.children.splice(parent.children.indexOf(c), 1);
    c.parent = null;
  }
}

/** w:pPr всегда первый потомок w:p. */
export function ensurePPr(p: XElement): XElement {
  const existing = firstChild(p, 'w:pPr');
  if (existing) return existing;
  return insertAt(p, el('w:pPr'), 0);
}

/** w:rPr всегда первый потомок w:r. */
export function ensureRPr(r: XElement): XElement {
  const existing = firstChild(r, 'w:rPr');
  if (existing) return existing;
  return insertAt(r, el('w:rPr'), 0);
}

/** Копия набора свойств без вложенной истории изменений — для w:pPrChange / w:rPrChange. */
export function snapshotProps(props: XElement | undefined, name: string, exclude: readonly string[]): XElement {
  const snap = el(name);
  if (!props) return snap;
  for (const c of props.children) {
    if (c.type === 'element' && exclude.includes(c.name)) continue;
    const copy = cloneNode(c);
    copy.parent = snap;
    snap.children.push(copy);
  }
  roundFractions(snap);
  return snap;
}

/**
 * Дробные значения там, где схема ждёт целое число («425.19685039370086» twip, «100.0» %),
 * оставляют некоторые конвертеры. Word их прощает, валидатор — нет; при правке округляем.
 */
export function roundFractions(e: XElement): void {
  for (const [k, v] of Object.entries(e.attrs)) if (/^-?\d+\.\d+$/.test(v)) e.attrs[k] = String(Math.round(Number(v)));
  for (const c of e.children) if (c.type === 'element') roundFractions(c);
}
